import { useCallback, useEffect, useState } from 'react';
import { getMyChallenges } from '../utils/incentiveChallengeApi';

/**
 * The signed-in user's active challenges, for the toolbar badge.
 *
 * Visibility is decided by the server: it returns only what this person is
 * measured on, or manages. The badge does not filter anything itself.
 *
 * Local state rather than a redux slice, matching the other toolbar entries:
 * nothing else needs this data.
 */
export default function useMyChallenges({ enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await getMyChallenges();
      if (res?.status !== 'success') {
        setData(null);
        return;
      }
      const shape = (rows) =>
        (rows || []).map((c) => ({
          id: c.incentive_challenge_id,
          name: c.name,
          type: c.type,
          assignmentLabel: c.assignment_label,
          target: Number(c.target),
          // What `achieved` is measured against. The same as `target` for the
          // two buckets the badge shows; different for a team row, where each
          // member carries the whole target.
          against: Number(c.against),
          // Only on a head's own row: the size of the team behind the figure.
          fromTeam: c.from_team,
          achieved: Number(c.achieved),
          gap: Number(c.gap),
          // The bar runs from nothing to the TOP rung, so the percentage is of
          // the whole ladder — not of the rung being chased.
          topTarget: c.top_target === null || c.top_target === undefined ? null : Number(c.top_target),
          percent:
            Number(c.top_target) > 0
              ? Math.round((Number(c.achieved) / Number(c.top_target)) * 100)
              : c.percent,
          // The one prize held — the highest rung cleared — and the one being
          // chased. Prizes do not add up, so there is never more than one held.
          prizeWon: c.prizes_won?.[0]?.prize || null,
          nextTier: c.next_tier || null,
          ladderComplete: Boolean(c.ladder_complete),
          // Partitioned challenges only. The figures above are then the round
          // under way; this says which round it is and how the rest went.
          partition: c.partition || null,
        }));
      setData({
        // The server decides who a challenge can ever name. False for Finance,
        // who set challenges rather than run in them, and for anyone whose role
        // a challenge cannot be assigned to.
        eligible: res.data.eligible !== false,
        individual: shape(res.data.individual),
        group: shape(res.data.group),
        team: shape(res.data.team),
      });
      setUpdatedAt(Date.now());
    } catch (error) {
      // A badge that cannot load is not worth interrupting anyone over — it
      // simply does not render.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, updatedAt, refresh: load };
}
